import {XCol, XText, XRow, XPush, uiStyles, cx, XTextButton} from "@cdx/common";
import {Link} from "react-router-dom";
import Avatar from "../../components/Avatar";
import {CdxCropImg, CdxImgForExternalUrl, CdxImgWithKnownDimensions} from "../../components/CdxImg";
import FromNow from "../../components/FromNow";
import Markdown from "../../components/Markdown";
import {useModalAdress} from "../../components/ModalRegistry";
import confirm from "../../lib/confirm";
import useMutation from "../../lib/hooks/useMutation";
import {useInstance} from "../../lib/mate/mate-utils";
import {embedStyles} from "./unified-card-container.css";
import {ReactComponent as GithubSvg} from "../../pages/account-settings/integrations/github/assets/github-logo-black.svg";
import {ReactComponent as BitbucketSvg} from "../../pages/account-settings/integrations/generic-scm/assets/bitbucket/bitbucket-logo-rgb.svg";
import {ReactComponent as GitlabSvg} from "../../pages/account-settings/integrations/generic-scm/assets/gitlab/gitlab-icon-rgb.svg";
import {Box, DSIconButton, DSIconClose, DSIconDiscord, Row} from "@cdx/ds";
import {makeScrollable} from "@cdx/ds/utils/makeScrollable";
import dsStyles from "@cdx/ds/css/index.css";

const Embed = ({idx, card, children, canModify, ...rest}) => {
  const [doUpdate] = useMutation("cards", "update");
  const cardId = card.id;
  const handleClick = () => {
    confirm({
      title: "Delete this message?",
      confirmLabel: "Yes, delete it",
      cancelLabel: "Cancel",
    }).then((ok) => {
      if (ok) doUpdate({id: cardId, embeds: card.embeds.filter((_, i) => i !== idx)});
    });
  };

  return (
    <XCol
      px={2}
      py={1}
      rounded="sm"
      bg="gray200"
      border="gray300"
      sp={1}
      className={uiStyles.hideContainer}
      relative
      {...rest}
    >
      {canModify && (
        <Box absolute top="-1px" right="-1px" className={uiStyles.hideElement}>
          <DSIconButton
            variant="tertiary"
            icon={<DSIconClose />}
            size="sm"
            negatePadding
            onClick={handleClick}
          />
        </Box>
      )}
      {children}
    </XCol>
  );
};

const DiscordMember = ({card, discordAuthorId}) => {
  const getMember = () => {
    if (!card.meta.discord?.slashCommandId) return null;
    const discordGuild = card.$meta.get("discordGuild", null);
    return (
      discordGuild &&
      discordGuild.$meta.find("members", {
        discordUserId: discordAuthorId,
      })[0]
    );
  };
  const member = getMember();
  if (!member)
    return (
      <XText size={1} preset="bold" color="gray600">
        user {discordAuthorId}
      </XText>
    );
  const discordUserId = member.$meta.get("discordUserId", null);
  const avatarId = member.$meta.get("avatar", null);

  const avatar =
    avatarId && discordUserId ? (
      <CdxCropImg
        title={member.nick || member.name}
        alt={member.nick || member.name}
        width={14}
        height={14}
        src={`https://cdn.discordapp.com/avatars/${discordUserId}/${avatarId}.png`}
        className={cx(uiStyles.rounded.full, uiStyles.noOverflow, uiStyles.position.relative)}
        style={{marginTop: -3, top: 3}}
      />
    ) : null;
  return (
    <>
      {avatar}
      <XText size={1} preset="bold" color="gray600">
        {member.nick || member.name}
      </XText>
    </>
  );
};

const DiscordAttachment = ({id, url, width, height, filename, contentType, ...rest}) => {
  const to = useModalAdress({
    modal: "imageViewer",
    state: {url, width, height, title: filename},
  });
  return (
    <>
      {(contentType || "").startsWith("image/") ? (
        <Link to={to}>
          {width && height ? (
            <CdxImgWithKnownDimensions
              key={id}
              src={url}
              width={width}
              height={height}
              maxWidth={200}
              maxHeight={80}
              className={embedStyles.discordEmbedImg}
            />
          ) : (
            <CdxImgForExternalUrl
              maxWidth={200}
              maxHeight={80}
              key={id}
              src={url}
              className={embedStyles.discordEmbedImg}
            />
          )}
        </Link>
      ) : (
        <XCol
          as="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          bg="gray300"
          border="gray400"
          rounded="sm"
          pa={2}
          align="center"
          justify="center"
          elevation={0}
        >
          <XText color="gray600" preset="bold" size={0} align="center">
            {filename || "unknown file name"}
          </XText>
        </XCol>
      )}
    </>
  );
};

const MultiBox = ({attachmentCount, children}) => {
  if (attachmentCount < 2) {
    return <Row className={makeScrollable({dir: "horizontal"})}>{children}</Row>;
  } else {
    return (
      <Row
        sp={1}
        py={1}
        rounded="sm"
        border="gray300"
        px={1}
        className={makeScrollable({dir: "horizontal"})}
      >
        {children}
      </Row>
    );
  }
};

const ThreadMessageEmbed = ({
  discordAuthorId,
  content,
  messageId,
  channelId,
  guildId,
  attachments,
  createdAt,
  lastEditedAt,
  card,
  idx,
  canModify,
}) => (
  <Embed idx={idx} card={card} canModify={canModify}>
    {(content || attachments.length === 0) && (
      <Markdown size="sm">{content || "No Content"}</Markdown>
    )}
    {attachments.length > 0 && (
      <MultiBox attachmentCount={attachments.length}>
        {attachments.map((att) => (
          <DiscordAttachment key={att.id} {...att} />
        ))}
      </MultiBox>
    )}
    <XRow align="baseline" sp={2}>
      <XRow align="baseline" sp={0}>
        <XText size={0} color="gray500">
          message by
        </XText>
        <DiscordMember card={card} discordAuthorId={discordAuthorId} />
      </XRow>
      <XPush />
      {lastEditedAt && (
        <XText size={0} color="gray500">
          (edited)
        </XText>
      )}
      <XText
        size={0}
        color="gray500"
        as="a"
        href={`https://discordapp.com/channels/${guildId}/${channelId}/${messageId}`}
        target="_blank"
        rel="noreferrer"
      >
        <FromNow date={new Date(createdAt)} />
      </XText>
      <XTextButton
        square
        href={`https://discordapp.com/channels/${guildId}/${channelId}/${messageId}`}
        target="_blank"
        style={{top: 4, marginTop: -4}}
      >
        <DSIconDiscord className={uiStyles.color.gray500} size={16} />
      </XTextButton>
    </XRow>
  </Embed>
);

const SourceMessageEmbed = ({
  discordAuthorId,
  messageId,
  channelId,
  guildId,
  attachments,
  createdAt,
  lastEditedAt,
  card,
  idx,
  canModify,
}) => (
  <Embed idx={idx} card={card} canModify={canModify}>
    <XRow align="baseline" sp={2} fillParent>
      <XRow align="baseline" sp={0}>
        <XText size={0} color="gray500">
          submitted by
        </XText>
        <DiscordMember card={card} discordAuthorId={discordAuthorId} />
      </XRow>
      <XPush />
      {lastEditedAt && (
        <XText size={0} color="gray500">
          (edited)
        </XText>
      )}
      <XText
        size={0}
        color="gray500"
        as="a"
        href={`https://discordapp.com/channels/${guildId}/${channelId}/${messageId}`}
        target="_blank"
        rel="noreferrer"
      >
        <FromNow date={new Date(createdAt)} />
      </XText>
      <XTextButton
        square
        href={`https://discordapp.com/channels/${guildId}/${channelId}/${messageId}`}
        target="_blank"
        style={{top: 4, marginTop: -4}}
      >
        <DSIconDiscord className={uiStyles.color.gray500} size={16} />
      </XTextButton>
    </XRow>
    {attachments.length > 0 && (
      <MultiBox attachmentCount={attachments.length}>
        {attachments.map((att, attIdx) => (
          <DiscordAttachment key={att.id || attIdx} contentType="image/jpg" {...att} />
        ))}
      </MultiBox>
    )}
  </Embed>
);

const UserReportEmbed = ({userReportEmailId, idx, card}) => {
  const instance = useInstance(
    "userReportEmail",
    process.env.REACT_APP_MODE === "open" ? null : userReportEmailId
  );
  return (
    <Embed idx={idx} card={card} canModify={false}>
      <XRow align="baseline" sp={1}>
        <XText size={0} color="gray500">
          Report by
        </XText>
        {instance ? (
          <XText size={1} preset="bold" as="a" href={`mailto:${instance.email}`}>
            {instance.email}
          </XText>
        ) : (
          <XText size={1} preset="bold" color="gray600">
            email not accessible
          </XText>
        )}
      </XRow>
    </Embed>
  );
};

const DeactivateEmbed = ({discordUserId, reason, createdAt, card, idx, canModify}) => (
  <Embed idx={idx} card={card} canModify={canModify}>
    <XRow align="baseline" sp={2} fillParent>
      <XRow align="baseline" sp={0}>
        <XText size={0} color="gray500">
          deactivated by
        </XText>
        <DiscordMember card={card} discordAuthorId={discordUserId} />
      </XRow>
      <XPush />
      <XText size={0} color="gray500">
        <FromNow date={new Date(createdAt)} />
      </XText>
      <DSIconDiscord
        className={cx(uiStyles.color.gray500, dsStyles.relative.true)}
        size={16}
        style={{top: 4, marginTop: -4}}
      />
    </XRow>
    {reason && <Markdown size="sm">{reason}</Markdown>}
  </Embed>
);

const SCMAvatar = ({userId}) => {
  const user = useInstance("user", userId);
  return (
    <>
      <Avatar user={user} size={14} style={{position: "relative", top: 3, marginTop: -3}} />
      <XText size={1} preset="bold" color="gray600" noOverflow style={{maxWidth: "7em"}}>
        {user ? user.name : "[unknown user]"}
      </XText>
    </>
  );
};

const SCMEmbed = ({
  card,
  provider,
  messageType,
  link,
  message,
  author,
  createdAt,
  idx,
  branch,
  canModify,
}) => {
  const messageTypeLabel = {comment: "commit by", review: "review requested by", done: "fixed by"};
  const IconComp =
    {github: GithubSvg, gitlab: GitlabSvg, bitbucket: BitbucketSvg}[provider] || GithubSvg;
  return (
    <Embed idx={idx} card={card} canModify={canModify}>
      <XRow align="baseline" sp={2} fillParent>
        <XRow align="baseline" sp={0}>
          <XText size={0} color="gray500">
            {messageTypeLabel[messageType]}
          </XText>
          {author.type === "codecksUser" ? (
            <SCMAvatar userId={author.id} />
          ) : (
            <XText size={1} preset="bold" color="gray600" noOverflow style={{maxWidth: "8em"}}>
              {author.name}
            </XText>
          )}
        </XRow>
        <XPush />
        <XRow align="baseline" sp={0}>
          <XText size={0} color="gray500">
            {branch}
          </XText>
          <XText size={0} color="gray500">
            ·
          </XText>
          <XText size={0} color="gray500">
            <FromNow date={new Date(createdAt)} />
          </XText>
        </XRow>
        <XTextButton square href={link} target="_blank" style={{top: 4, marginTop: -4}}>
          <IconComp color="gray500" style={{height: 14, width: "auto"}} />
        </XTextButton>
      </XRow>
      <Markdown size="sm">{message}</Markdown>
    </Embed>
  );
};

const embedTypeToComp = {
  discordThreadMessage: ThreadMessageEmbed,
  discordSourceMessage: SourceMessageEmbed,
  discordDeactivateMessage: DeactivateEmbed,
  userReportEmail: UserReportEmbed,
  scmCommit: SCMEmbed,
};

const UnknownEmbed = ({type, content, idx, card, canModify}) => (
  <Embed idx={idx} card={card} canModify={canModify}>
    <XText color="gray700" size={1} preset="bold">
      Unknown embed {type}
    </XText>
    {content && <Markdown size="sm">{content}</Markdown>}
  </Embed>
);

const CardEmbeds = ({card, canModify}) => {
  const userReportEmailId = card.meta.userReport?.userReportEmailId ?? null;

  const implicitEmbeds = [userReportEmailId && {type: "userReportEmail", userReportEmailId}].filter(
    Boolean
  );
  const embeds = [...card.embeds, ...implicitEmbeds];
  if (embeds.length === 0) return null;

  return (
    <XCol
      pb={7}
      px={2}
      pt={5}
      sp={0}
      onClick={(e) => e.stopPropagation()}
      className={dsStyles.mt.auto}
    >
      {embeds.map((embed, idx) => {
        const Comp = embedTypeToComp[embed.type] || UnknownEmbed;
        return <Comp key={idx} idx={idx} card={card} canModify={canModify} {...embed} />;
      })}
    </XCol>
  );
};

export default CardEmbeds;
